import { HStack, type StackProps } from '@chakra-ui/react';
import { useEffect, useState, type FC } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { BrightnessInputElement } from '$/components/core/Collection/CollectionFilter/components/BrightnessFilter/BrightnessInputElement';
import { BrightnessSlider } from '$/components/core/Collection/CollectionFilter/components/BrightnessFilter/BrightnessSlider';
import { type MaterialBrightnessFilter } from '$/services/usecases/materials';
import { useCollectionStore } from '$/stores/useCollectionStore';

interface Props extends StackProps {
  onBrightnessChange?: (value: MaterialBrightnessFilter) => void;
}

export const BrightnessFilterMenuItem: FC<Props> = ({
  onBrightnessChange,
  ...rest
}) => {
  const brightnessFilter = useCollectionStore.useBrightnessFilter();

  const [value, setValue] = useState(brightnessFilter);

  const debounced = useDebouncedCallback((value: MaterialBrightnessFilter) => {
    useCollectionStore.getState().setBrightnessFilter(value);
  }, 250);

  useEffect(() => {
    setValue(brightnessFilter);
    onBrightnessChange?.(brightnessFilter);
  }, [brightnessFilter, onBrightnessChange]);

  return (
    <HStack gap='4' p='3' {...rest}>
      <BrightnessInputElement
        value={value[0]}
        validate={(curr) => curr >= 0 && curr <= 100 && curr < value[1]}
        transformer={(curr) =>
          curr <= 0 ? 0 : curr >= value[1] ? value[1] - 1 : curr
        }
        onChange={(newValue) => {
          setValue([newValue, value[1]]);
          debounced([newValue, value[1]]);
        }}
      />
      <BrightnessSlider
        value={value}
        setValue={(val) => {
          setValue(val);
          debounced(val);
        }}
      />
      <BrightnessInputElement
        value={value[1]}
        validate={(curr) => curr >= 0 && curr <= 100 && curr > value[0]}
        transformer={(curr) =>
          curr >= 100 ? 100 : curr <= value[0] ? value[0] + 1 : curr
        }
        onChange={(newValue) => {
          setValue([value[0], newValue]);
          debounced([value[0], newValue]);
        }}
      />
    </HStack>
  );
};
